import 'react-phone-number-input/style.css';

import {
    type Gender,
    type NextLocale,
    type UserData,
    GENDER,
    NEXT_LOCALES,
} from '@beat-venues/common-types';
import {
    Button,
    Dropdown,
    Form,
    InputField,
    Text,
} from '@beat-venues/ui/react';
import { type SyntheticEvent, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { Link } from 'react-router-dom';
import { isPhoneValid } from '../../utils/phone-util';

export interface UpdateUserFormProps {
    user: Omit<UserData, 'sfContactId'>;
    onSubmit: (user: Omit<UserData, 'sfContactId'>) => void;
    onExit: () => void;
    isPending?: boolean;
    isError?: boolean;
}

export const UpdateUserForm = ({
    user,
    onSubmit,
    onExit,
    isPending,
    isError,
}: UpdateUserFormProps): JSX.Element => {
    const { t } = useTranslation();

    const [userDetails, setUserDetails] = useState<UserData>(user);
    const [isBDFocused, setIsBDFocused] = useState(false);
    const [isPhoneFocused, setIsPhoneFocused] = useState(false);
    const maxDate = new Date().toISOString().split('T')[0];

    const handleSubmit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (isPhoneValid(userDetails.mobilePhone || '')) {
            onSubmit(userDetails);
        }
    };

    return (
        <Form
            className="m-auto mb-8 mt-4 flex w-full flex-col rounded-md px-4 py-9 md:bg-gray-500 md:px-12"
            onSubmit={handleSubmit}
        >
            {isError && (
                <Text className="bg-la-brand-500 mb-2 rounded-sm p-2 pl-4">
                    {t('messages.errorUpdate')}
                </Text>
            )}
            {user &&
                Object.entries(user)
                    .filter(([key]) => key !== 'email')
                    .map(([key, value]) => (
                        <div key={`user-info-${key}-${value}`}>
                            {!['gender', 'language', 'mobilePhone'].includes(
                                key
                            ) ? (
                                <InputField
                                    className="mb-6"
                                    disabled={key === 'email'}
                                    label={t(`userAccount.${key}`) || ''}
                                    max={
                                        key === 'birthdate'
                                            ? maxDate
                                            : undefined
                                    }
                                    maxLength={60}
                                    name={key}
                                    required={[
                                        'firstName',
                                        'lastName',
                                    ].includes(key)}
                                    type={key === 'birthdate' ? 'date' : 'text'}
                                    value={value}
                                    onChange={(newValue) =>
                                        setUserDetails((prevState) => ({
                                            ...prevState,
                                            [key]: newValue.trim(),
                                        }))
                                    }
                                    onFocus={() => {
                                        if (key === 'birthdate') {
                                            setIsBDFocused(true);
                                        }
                                    }}
                                />
                            ) : key === 'gender' ? (
                                <Dropdown
                                    layout="mybeat"
                                    className="mb-6"
                                    defaultValue={
                                        GENDER.includes(value as Gender)
                                            ? value
                                            : ''
                                    }
                                    emptyTitle={
                                        t('userAccount.emptyDropdownTitle') ??
                                        ''
                                    }
                                    id={key}
                                    label={t(`userAccount.${key}`) || ''}
                                    name={key}
                                    options={GENDER.map((gender) => ({
                                        key: gender,
                                        value: t(`genderOptions.${gender}`),
                                    }))}
                                    onChange={(newValue) =>
                                        setUserDetails((prevState) => ({
                                            ...prevState,
                                            gender: newValue as Gender,
                                        }))
                                    }
                                />
                            ) : key === 'language' ? (
                                <Dropdown
                                    layout="mybeat"
                                    className="mb-6"
                                    defaultValue={
                                        value &&
                                        NEXT_LOCALES.includes(
                                            value as NextLocale
                                        )
                                            ? value
                                            : ''
                                    }
                                    emptyTitle={
                                        t('userAccount.emptyDropdownTitle') ??
                                        ''
                                    }
                                    id={key}
                                    label={t(`userAccount.${key}`) || ''}
                                    name={key}
                                    options={NEXT_LOCALES.map((locale) => ({
                                        key: locale,
                                        value: t(`language.${locale}`),
                                    }))}
                                    required={true}
                                    onChange={(newValue) =>
                                        setUserDetails((prevState) => ({
                                            ...prevState,
                                            language: newValue as NextLocale,
                                        }))
                                    }
                                />
                            ) : (
                                <>
                                    <label
                                        className="mb-2 pl-5 opacity-70"
                                        htmlFor="mobilePhone"
                                    >
                                        {t(`userAccount.${key}`)}
                                    </label>
                                    <PhoneInput
                                        international
                                        className="react-international-phone-input-container"
                                        defaultCountry="BE"
                                        id="mobilePhone"
                                        name="mobilePhone"
                                        value={user.mobilePhone}
                                        onChange={(newValue) =>
                                            setUserDetails((prevState) => ({
                                                ...prevState,
                                                mobilePhone: newValue ?? '',
                                            }))
                                        }
                                        onFocus={() => setIsPhoneFocused(true)}
                                    />
                                    {isPhoneFocused && (
                                        <div className="-mt-3 mb-2 pl-5 text-sm text-gray-100">
                                            {t('messages.MobileExplanation')}
                                        </div>
                                    )}
                                    {!isPhoneValid(
                                        userDetails.mobilePhone || ''
                                    ) && (
                                        <div className="text-la-brand-500 -mt-2 mb-1 pl-5">
                                            {t('messages.invalidMobile')}
                                        </div>
                                    )}
                                </>
                            )}
                            {isBDFocused && key === 'birthdate' && (
                                <div className="-mt-3 mb-1 pl-5 text-sm text-gray-100">
                                    {t('messages.birthdateExplanationFirst')}
                                    <Link
                                        className="underline"
                                        to={`/${t('routes.preferences')}`}
                                    >
                                        {t('messages.birthdateExplanationLink')}
                                    </Link>
                                    {t('messages.birthdateExplanationSecond')}
                                </div>
                            )}
                        </div>
                    ))}
            <div className="flex flex-row gap-4">
                <Button disabled={isPending} intent="primary" type="submit">
                    {t('userAccount.submit')}
                </Button>

                <Button
                    disabled={isPending}
                    intent="secondary"
                    onClick={() => onExit()}
                >
                    {t('userAccount.exit')}
                </Button>
            </div>
        </Form>
    );
};
